"use client";

// THE DECK — a row of cards you swipe, drag or step through, left-aligned from
// the shelf's edge, with the card at the front named by `onAt`. One component
// for every deck on the commons: a folder of skills (PackRoom) and a chapter of
// guides (GuideRoom). What reads beneath the deck is the caller's; the deck only
// owns the row, the steps, and which card is in front.
//
// ⚠️ A CARD IS A LINK, AND A LINK IS DRAGGABLE (2026-09-29). Pressing a card
// with a mouse and moving started the browser's own link-drag (the ghost of the
// url following the pointer) before the deck's drag could, so a card came away
// "as a link" instead of the row sliding. The deck now refuses the native drag
// outright (dragstart is cancelled, and links/images inside it carry
// -webkit-user-drag: none), so a mouse press that moves is always the row.

import { useCallback, useEffect, useImperativeHandle, useRef, useState, type Ref } from "react";

export interface DeckHandle {
  go: (i: number) => void;
}

function Step({ dir }: { dir: -1 | 1 }) {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
      <path d={dir < 0 ? "M8.6 3.2 4.8 7l3.8 3.8" : "M5.4 3.2 9.2 7l-3.8 3.8"} stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function Deck({
  count,
  keyOf,
  renderCard,
  onAt,
  initial = 0,
  label,
  noun = "card",
  handle,
  wide,
}: {
  count: number;
  keyOf: (i: number) => string;
  renderCard: (i: number, front: boolean) => React.ReactNode;
  /** the card now in front, and the side it came from */
  onAt?: (i: number, dir: number) => void;
  /** the card to open on (a deep link to one step of a chapter) */
  initial?: number;
  label: string;
  noun?: string;
  handle?: Ref<DeckHandle>;
  /** wider slots (guide cards carry a picture) */
  wide?: boolean;
}) {
  const track = useRef<HTMLDivElement>(null);
  const [at, setAt] = useState(initial);
  const atRef = useRef(initial);

  const nearest = useCallback((): number => {
    const el = track.current;
    if (!el) return 0;
    const pad = parseFloat(getComputedStyle(el).paddingLeft) || 0;
    let best = 0;
    let bestD = Infinity;
    el.querySelectorAll<HTMLElement>(":scope > .cm-deck__slot").forEach((slot, i) => {
      const d = Math.abs(slot.offsetLeft - pad - el.scrollLeft);
      if (d < bestD) { bestD = d; best = i; }
    });
    return best;
  }, []);

  const go = useCallback((i: number, instant = false) => {
    const el = track.current;
    if (!el) return;
    const next = Math.max(0, Math.min(count - 1, i));
    const slot = el.querySelectorAll<HTMLElement>(":scope > .cm-deck__slot")[next];
    if (!slot) return;
    const pad = parseFloat(getComputedStyle(el).paddingLeft) || 0;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    el.scrollTo({ left: slot.offsetLeft - pad, behavior: instant || reduce ? "auto" : "smooth" });
  }, [count]);
  useImperativeHandle(handle, () => ({ go: (i: number) => go(i) }), [go]);

  // open on the card the address asked for, without a glide
  useEffect(() => {
    if (initial > 0) go(initial, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const el = track.current;
    if (!el) return;
    let raf = 0;
    const read = () => {
      raf = 0;
      const i = nearest();
      if (i !== atRef.current) {
        const dir = i > atRef.current ? 1 : -1;
        atRef.current = i;
        setAt(i);
        onAt?.(i, dir);
      }
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(read); };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => { el.removeEventListener("scroll", onScroll); if (raf) cancelAnimationFrame(raf); };
  }, [count, nearest, onAt]);

  // a mouse DRAGS the row (a finger already swipes it natively). Snap lets go
  // while the hand is on it and takes over again on release, leaning the way
  // the hand moved. A press that travels more than a few pixels is not a click.
  const drag = useRef<{ x: number; left: number; moved: boolean; from: number } | null>(null);
  const onPointerDown = (e: React.PointerEvent) => {
    if (e.pointerType !== "mouse" || e.button !== 0) return;
    if ((e.target as HTMLElement).closest("button, input, textarea, [data-noswipe]")) return;
    const el = track.current;
    if (!el) return;
    drag.current = { x: e.clientX, left: el.scrollLeft, moved: false, from: atRef.current };
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current;
    const el = track.current;
    if (!d || !el) return;
    const dx = e.clientX - d.x;
    if (!d.moved && Math.abs(dx) > 5) {
      d.moved = true;
      el.dataset.dragging = "";
      try { el.setPointerCapture(e.pointerId); } catch { /* the pointer is gone */ }
    }
    if (d.moved) el.scrollLeft = d.left - dx;
  };
  const endDrag = (e: React.PointerEvent) => {
    const d = drag.current;
    const el = track.current;
    drag.current = null;
    if (!d || !el || !d.moved) return;
    delete el.dataset.dragging;
    try { el.releasePointerCapture(e.pointerId); } catch { /* already released */ }
    let best = nearest();
    const lean = e.clientX - d.x;
    if (best === d.from && Math.abs(lean) > 40) best = d.from + (lean < 0 ? 1 : -1);
    go(best);
    // swallow the click the release would otherwise send to the card's link
    const stop = (ev: Event) => { ev.stopPropagation(); ev.preventDefault(); };
    el.addEventListener("click", stop, { capture: true, once: true });
    window.setTimeout(() => el.removeEventListener("click", stop, { capture: true }), 0);
  };

  // a card at the side comes to the front when pressed; the one in front opens
  const onSlotClick = (i: number) => (e: React.MouseEvent) => {
    if (i === atRef.current) return;
    if ((e.target as HTMLElement).closest("button")) return;
    e.preventDefault();
    e.stopPropagation();
    go(i);
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowRight") { e.preventDefault(); go(atRef.current + 1); }
    else if (e.key === "ArrowLeft") { e.preventDefault(); go(atRef.current - 1); }
  };

  return (
    <div className="cm-deck" data-wide={wide || undefined}>
      <div className="cm-deck__bar">
        <span className="cm-deck__at" aria-live="polite">
          {at + 1} <span>of {count}</span>
        </span>
        <span className="cm-deck__steps">
          <button type="button" className="icon-circle cm-deck__step" onClick={() => go(at - 1)} disabled={at === 0} aria-label={`the ${noun} before`}>
            <Step dir={-1} />
          </button>
          <button type="button" className="icon-circle cm-deck__step" onClick={() => go(at + 1)} disabled={at >= count - 1} aria-label={`the next ${noun}`}>
            <Step dir={1} />
          </button>
        </span>
      </div>
      <div
        ref={track}
        className="cm-deck__track"
        role="region"
        aria-roledescription="carousel"
        aria-label={label}
        tabIndex={0}
        onKeyDown={onKey}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onDragStart={(e) => e.preventDefault()}
      >
        {Array.from({ length: count }, (_, i) => (
          <div
            key={keyOf(i)}
            className="cm-deck__slot"
            data-front={i === at || undefined}
            aria-roledescription="slide"
            aria-label={`${i + 1} of ${count}`}
            onClickCapture={onSlotClick(i)}
          >
            {renderCard(i, i === at)}
          </div>
        ))}
      </div>
    </div>
  );
}
